import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Check, X, RefreshCw, CalendarOff } from "lucide-react";

import { Button } from "../components/ui/button";
import { api } from "../services/api";

// Absence types — values mirror backend AbsenceType enum
const TYPE_OPTIONS = [
  "paid_leave", "unpaid_leave", "sick_leave", "training",
  "business_trip", "maternity", "paternity", "bereavement", "hajj", "other",
];

const STATUS_FILTERS = ["pending", "approved", "rejected", "all"];

const statusStyles = {
  pending: "bg-amber-100 text-amber-700 border-amber-200",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  cancelled: "bg-muted text-muted-foreground border-border",
};

const AbsenceRequests = () => {
  const { t } = useTranslation();

  const [statusFilter, setStatusFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const [showCreate, setShowCreate] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    employee_id: "",
    absence_type: "paid_leave",
    start_date: "",
    end_date: "",
    reason: "",
  });

  const typeLabel = (val) => t(`portal.leaves.types.${val}`, val);
  const statusLabel = (val) =>
    t(`absenceRequests.status.${val}`, {
      pending: "En attente", approved: "Approuvée",
      rejected: "Rejetée", cancelled: "Annulée",
    }[val] || val);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await api.get("v1/absences", { params });
      setItems(res.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail ?? t("common.operationError", "Une erreur est survenue"));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => { load(); }, [load]);

  const loadEmployees = async () => {
    try {
      const r = await api.get("v1/employees/", { params: { limit: 200 } });
      setEmployees(r.data.employees || r.data.items || r.data || []);
    } catch {
      setEmployees([]);
    }
  };

  const openCreate = () => {
    setForm({ employee_id: "", absence_type: "paid_leave", start_date: "", end_date: "", reason: "" });
    setShowCreate(true);
    loadEmployees();
  };

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`v1/absences/${id}/approve`, { approval_comment: null });
      toast.success(t("absenceRequests.approved", "Absence approuvée"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? t("common.operationError", "Une erreur est survenue"));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    const comment = window.prompt(
      t("absenceRequests.rejectPrompt", "Motif du rejet (obligatoire) :")
    );
    if (comment === null) return;
    if (!comment.trim()) {
      toast.error(t("absenceRequests.rejectReasonRequired", "Le motif de rejet est obligatoire"));
      return;
    }
    setBusyId(id);
    try {
      await api.post(`v1/absences/${id}/reject`, { approval_comment: comment.trim() });
      toast.success(t("absenceRequests.rejected", "Absence rejetée"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? t("common.operationError", "Une erreur est survenue"));
    } finally {
      setBusyId(null);
    }
  };

  const submitCreate = async (e) => {
    e.preventDefault();
    if (!form.employee_id) {
      toast.error(t("absenceRequests.employeeRequired", "Sélectionnez un employé"));
      return;
    }
    if (!form.start_date || !form.end_date) {
      toast.error(t("portal.leaves.toasts.datesRequired", "Les dates sont obligatoires"));
      return;
    }
    if (new Date(form.end_date) < new Date(form.start_date)) {
      toast.error(t("portal.leaves.toasts.endBeforeStart", "La date de fin précède la date de début"));
      return;
    }
    setSaving(true);
    try {
      await api.post("v1/absences", {
        employee_id: Number(form.employee_id),
        absence_type: form.absence_type,
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason || null,
      });
      toast.success(t("absenceRequests.created", "Demande d'absence créée"));
      setShowCreate(false);
      setStatusFilter("pending");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail ?? t("common.operationError", "Une erreur est survenue"));
    } finally {
      setSaving(false);
    }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  return (
    <div className="space-y-4">
      {/* Header row */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">
            {t("absenceRequests.title", "Demandes d'absence")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("absenceRequests.subtitle", "Validez ou rejetez les demandes, ou enregistrez une absence pour un collaborateur")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            {t("common.refresh", "Actualiser")}
          </Button>
          <Button size="sm" onClick={openCreate}>
            <Plus className="w-4 h-4 mr-1.5" />
            {t("absenceRequests.new", "Nouvelle absence")}
          </Button>
        </div>
      </div>

      {/* Status filter */}
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${
              statusFilter === s
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-foreground border-border hover:bg-accent"
            }`}
          >
            {s === "all" ? t("absenceRequests.filterAll", "Toutes") : statusLabel(s)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left text-muted-foreground">
              <th className="px-4 py-2.5 font-medium">{t("absenceRequests.col.employee", "Employé")}</th>
              <th className="px-4 py-2.5 font-medium">{t("absenceRequests.col.type", "Type")}</th>
              <th className="px-4 py-2.5 font-medium">{t("absenceRequests.col.period", "Période")}</th>
              <th className="px-4 py-2.5 font-medium text-center">{t("absenceRequests.col.days", "Jours")}</th>
              <th className="px-4 py-2.5 font-medium">{t("absenceRequests.col.status", "Statut")}</th>
              <th className="px-4 py-2.5 font-medium text-right">{t("absenceRequests.col.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">{t("common.loading", "Chargement…")}</td></tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">
                  <CalendarOff className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  {t("absenceRequests.empty", "Aucune demande pour ce filtre")}
                </td>
              </tr>
            ) : (
              items.map((a) => (
                <tr key={a.id} className="border-t border-border hover:bg-accent/40">
                  <td className="px-4 py-2.5 font-medium">{a.employee_name || `#${a.employee_id}`}</td>
                  <td className="px-4 py-2.5">{typeLabel(a.absence_type)}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">{fmt(a.start_date)} → {fmt(a.end_date)}</td>
                  <td className="px-4 py-2.5 text-center">{a.days_count}</td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${statusStyles[a.status] || statusStyles.cancelled}`}>
                      {statusLabel(a.status)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5">
                    {a.status === "pending" ? (
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm" variant="outline" disabled={busyId === a.id}
                          className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                          onClick={() => approve(a.id)}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          {t("absenceRequests.approve", "Approuver")}
                        </Button>
                        <Button
                          size="sm" variant="outline" disabled={busyId === a.id}
                          className="text-red-700 border-red-200 hover:bg-red-50"
                          onClick={() => reject(a.id)}
                        >
                          <X className="w-4 h-4 mr-1" />
                          {t("absenceRequests.reject", "Rejeter")}
                        </Button>
                      </div>
                    ) : (
                      <div className="text-right text-muted-foreground text-xs">
                        {a.approved_by_name ? `${statusLabel(a.status)} · ${a.approved_by_name}` : "—"}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-xl w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="font-semibold">{t("absenceRequests.newTitle", "Nouvelle absence")}</h2>
              <button onClick={() => setShowCreate(false)} className="text-muted-foreground hover:text-foreground">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submitCreate} className="p-4 space-y-4">
              <div>
                <label className="text-sm font-medium">{t("absenceRequests.col.employee", "Employé")}</label>
                <select
                  className="w-full mt-1 border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                  value={form.employee_id}
                  onChange={(e) => setForm((f) => ({ ...f, employee_id: e.target.value }))}
                  required
                >
                  <option value="">{t("absenceRequests.selectEmployee", "— Sélectionner —")}</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name}{emp.registration_number ? ` (${emp.registration_number})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium">{t("portal.leaves.type", "Type")}</label>
                <select
                  className="w-full mt-1 border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                  value={form.absence_type}
                  onChange={(e) => setForm((f) => ({ ...f, absence_type: e.target.value }))}
                >
                  {TYPE_OPTIONS.map((val) => (
                    <option key={val} value={val}>{typeLabel(val)}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium">{t("portal.leaves.startDate", "Début")}</label>
                  <input
                    type="date"
                    className="w-full mt-1 border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                    value={form.start_date}
                    onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">{t("portal.leaves.endDate", "Fin")}</label>
                  <input
                    type="date"
                    className="w-full mt-1 border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                    value={form.end_date}
                    onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                    required
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium">{t("absenceRequests.reason", "Motif (optionnel)")}</label>
                <textarea
                  className="w-full mt-1 border border-border rounded-md px-3 py-2 text-sm bg-background text-foreground"
                  rows={2}
                  value={form.reason}
                  onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                  {t("common.cancel", "Annuler")}
                </Button>
                <Button type="submit" disabled={saving}>
                  {saving ? t("common.saving", "Enregistrement…") : t("common.create", "Créer")}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AbsenceRequests;
